import {
  createDocFromUint8Array,
  type ParticipantNote,
  type ParticipantNoteVersion,
} from '@piccolohealth/pbs-common';
import { ChakraV3, dataAttr, FancyDate, ScrollArea } from '@piccolohealth/ui';
import { inflection, P } from '@piccolohealth/util';
import React from 'react';
import { createModal } from '../../components/generic/Modal';
import { Editor } from '../../components/tiptap/Editor';
import { useEditor } from '../../components/tiptap/hooks/useEditor';
import type { HocuspocusState } from '../../components/tiptap/hooks/useHocuspocusProvider';
import { EditorLayout } from '../../components/tiptap/layout/EditorLayout';
import {
  convertYDocToJSONContent,
  generateHTML,
  getParticipantNoteExtensions,
  postProcessContent,
} from '../../components/tiptap/utils';
import { UserAvatar } from '../../components/user/UserAvatar';
import {
  useParticipantNoteVersionQuery,
  useParticipantNoteVersionsInfiniteQuery,
} from '../../graphql/hooks/useParticipantQuery';
import { useRestoreParticipantNoteVersionMutation } from '../../graphql/hooks/useRestoreParticipantNoteVersionMutation';
import { useAppContext } from '../../hooks/useAppContext';
import { scrollIntoView } from '../../utils/scrollIntoView';

interface ParticipantNoteHistorySidebarProps {
  participantId: string;
  participantNoteId: string;
  activeVersionId: string | null;
  setActiveVersionId: (versionId: string) => void;
}

const ParticipantNoteHistorySidebar = (props: ParticipantNoteHistorySidebarProps) => {
  const { participantId, participantNoteId, activeVersionId, setActiveVersionId } = props;
  const { organization } = useAppContext();

  const endRef = React.useRef<HTMLDivElement>(null);

  const participantNoteVersionsQuery = useParticipantNoteVersionsInfiniteQuery({
    organizationId: organization.id,
    participantId,
    participantNoteId,
    request: {
      pagination: {
        limit: 40,
      },
    },
  });

  const participantNoteVersionsPagination = P.first(participantNoteVersionsQuery.data?.pages ?? [])
    ?.organization?.participant?.note?.versions?.pagination;

  const participantNoteVersions =
    participantNoteVersionsQuery.data?.pages.flatMap(
      (page) => page.organization?.participant?.note?.versions.results as ParticipantNoteVersion[],
    ) ?? [];

  const latestVersion = P.first(participantNoteVersions);
  const isLoading = participantNoteVersionsQuery.isLoading;
  const hasNextPage = participantNoteVersionsQuery.hasNextPage;
  const total = participantNoteVersionsPagination?.total ?? 0;

  const fetchNextPage = () => {
    participantNoteVersionsQuery.fetchNextPage();
  };

  // https://github.com/biomejs/biome/issues/630
  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to run this effect when the number of pages changes
  React.useEffect(() => {
    if (endRef.current) {
      scrollIntoView(endRef.current);
    }
  }, [participantNoteVersionsQuery.data?.pages.length]);

  React.useEffect(() => {
    // TODO: Remove the active version null check when we make the active version id required
    if (!P.isNil(latestVersion) && !P.isNil(latestVersion.versionId)) {
      setActiveVersionId(latestVersion.versionId);
    }
  }, [latestVersion, setActiveVersionId]);

  if (!participantNoteVersionsPagination) {
    return null;
  }

  const content = P.run(() => {
    if (P.isEmpty(participantNoteVersions)) {
      return (
        <ChakraV3.Text fontSize='xs' fontWeight='bold' color='secondary'>
          No versions found
        </ChakraV3.Text>
      );
    }

    return (
      <>
        <ChakraV3.Text fontSize='xs' fontWeight='bold' color='secondary'>
          Versions
        </ChakraV3.Text>
        <ScrollArea flexDir='column' overflowY='auto' h='full'>
          <ChakraV3.Stack pr={4}>
            {participantNoteVersions.map((note, index) => {
              return (
                <ChakraV3.Button
                  onClick={() => {
                    // TODO: Remove the active version null check when we make the active version id required
                    if (!P.isNil(note.versionId)) {
                      setActiveVersionId(note.versionId);
                    }
                  }}
                  as={ChakraV3.HStack}
                  key={note.versionId}
                  {...{ 'data-active': dataAttr(activeVersionId === note.versionId) }}
                  variant='ghost'
                  color='gray.600'
                  size='sm'
                  justifyContent='start'
                  whiteSpace='initial'
                  h='auto'
                  px={3}
                  py={2}
                  rounded='md'
                  fontWeight='semibold'
                >
                  <ChakraV3.HStack w='full' align='start'>
                    <ChakraV3.Stack gap={1}>
                      <ChakraV3.Text fontWeight='semibold'>Version {total - index}</ChakraV3.Text>
                      <FancyDate
                        fontSize='xs'
                        color='secondary'
                        date={note.createdAt}
                        showTooltip={false}
                        showHumanized={false}
                        direction='row'
                      />
                    </ChakraV3.Stack>
                    <ChakraV3.Spacer />
                    <ChakraV3.AvatarGroup gap={-3}>
                      {note.users.map((member) => (
                        <UserAvatar
                          key={member.id}
                          name={member.name}
                          secondary={member.email}
                          picture={member.picture}
                          showTooltip
                          size='sm'
                        />
                      ))}
                    </ChakraV3.AvatarGroup>
                  </ChakraV3.HStack>
                </ChakraV3.Button>
              );
            })}
            <ChakraV3.Stack ref={endRef} />
          </ChakraV3.Stack>
        </ScrollArea>
        <ChakraV3.Spacer />
        <ChakraV3.Separator />
        <ChakraV3.HStack pr={4}>
          <ChakraV3.Text fontSize='xs' whiteSpace='nowrap'>
            {participantNoteVersionsPagination.total}{' '}
            {inflection.inflect('version', participantNoteVersionsPagination.total)}
          </ChakraV3.Text>
          <ChakraV3.Spacer minW={4} />
          <ChakraV3.Button
            onClick={fetchNextPage}
            variant='plain'
            fontSize='xs'
            disabled={!hasNextPage}
            loading={isLoading}
            justifyContent='end'
          >
            Load more
          </ChakraV3.Button>
        </ChakraV3.HStack>
      </>
    );
  });

  return (
    <ChakraV3.Stack
      h='full'
      w='280px'
      gap={4}
      flexShrink={0}
      borderRightWidth='1px'
      borderRightColor='gray.200'
    >
      {content}
    </ChakraV3.Stack>
  );
};

interface Props {
  note: ParticipantNote;
}

export const ParticipantNoteHistoryModal = createModal<Props>((props) => {
  const { note, modal } = props;

  const { remove, hide, visible } = modal;
  const { organization, successToast, errorToast, location } = useAppContext();

  const [activeVersionId, setActiveVersionId] = React.useState<string | null>(null);

  const restoreParticipantDocumentVersionMutation = useRestoreParticipantNoteVersionMutation();

  const activeParticipantNoteQuery = useParticipantNoteVersionQuery(
    {
      organizationId: organization.id,
      participantId: note.participantId,
      participantNoteId: note.id,
      versionId: activeVersionId ?? '',
    },
    {
      enabled: activeVersionId !== null,
    },
  );

  const activeParticipantNote = activeParticipantNoteQuery.data?.organization?.participant?.note;

  const extensions = getParticipantNoteExtensions({});

  const content = React.useMemo(() => {
    const valueBuffer = activeParticipantNote?.value ?? undefined;

    if (!valueBuffer) {
      return undefined;
    }

    const document = createDocFromUint8Array(valueBuffer.data);
    const jsonContent = convertYDocToJSONContent(document);
    const processed = postProcessContent(jsonContent, []);
    const htmlContent = generateHTML(processed, extensions);

    return htmlContent;
  }, [activeParticipantNote?.value, extensions]);

  const { editor } = useEditor(
    {
      extensions,
      isEditable: false,
      content,
    },
    [activeParticipantNote],
  );

  React.useEffect(() => {
    if (editor && !editor.isDestroyed && content && content !== editor.getHTML()) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  const onRestoreVersion = React.useCallback(async () => {
    await restoreParticipantDocumentVersionMutation
      .mutateAsync({
        organizationId: organization.id,
        participantNoteId: note.id,
        participantId: note.participantId,
        versionId: activeVersionId ?? '',
      })
      .then(() => {
        hide();
        successToast('Version restored successfully');
        location.reload();
      })
      .catch((err) => {
        errorToast(`Error restoring version: ${err.message}`);
      });
  }, [
    activeVersionId,
    note.id,
    note.participantId,
    organization.id,
    restoreParticipantDocumentVersionMutation,
    location,
    errorToast,
    hide,
    successToast,
  ]);

  const state: HocuspocusState = P.run(() => {
    if (activeParticipantNoteQuery.isFetching) {
      return { status: 'connecting' };
    }

    if (activeParticipantNoteQuery.isError) {
      return { status: 'disconnected' };
    }

    return { status: 'connected', save: { saveStatus: 'saved', saveCount: 0, lastSavedAt: null } };
  });

  return (
    <ChakraV3.Dialog.Root
      open={visible}
      onOpenChange={hide}
      onExitComplete={remove}
      size='cover'
      scrollBehavior='inside'
    >
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <ChakraV3.Dialog.Content>
          <ChakraV3.DialogTitleSubtitle
            title='Note - History'
            subtitle='You can view the history of this note here.'
          />
          <ChakraV3.Dialog.Body>
            <ChakraV3.HStack w='full' h='full' gap={4}>
              <ParticipantNoteHistorySidebar
                participantId={note.participantId}
                participantNoteId={note.id}
                activeVersionId={activeVersionId}
                setActiveVersionId={setActiveVersionId}
              />
              <ChakraV3.Stack w='full' h='full' align='center'>
                <ChakraV3.Stack h='full' w='full' overflowY='auto' layerStyle='fade'>
                  {activeParticipantNote && (
                    <EditorLayout
                      documentId={note.id}
                      isSidebarOpen={false}
                      state={state}
                      headerContent={null}
                      toolbarContent={null}
                      mainContent={<Editor editor={editor} />}
                      sidebarContent={null}
                    />
                  )}
                </ChakraV3.Stack>
                <ChakraV3.AlertBanner
                  status='warning'
                  alignItems='center'
                  fontSize='md'
                  title='Restoring this version will overwrite the current note. Are you sure you want to
                    restore this version?'
                  endElement={
                    <ChakraV3.HStack>
                      <ChakraV3.Button onClick={hide} size='sm' variant='subtle'>
                        Close
                      </ChakraV3.Button>
                      <ChakraV3.Button
                        onClick={onRestoreVersion}
                        colorPalette='brand'
                        size='sm'
                        loading={restoreParticipantDocumentVersionMutation.isLoading}
                      >
                        Restore version
                      </ChakraV3.Button>
                    </ChakraV3.HStack>
                  }
                />
              </ChakraV3.Stack>
            </ChakraV3.HStack>
          </ChakraV3.Dialog.Body>
        </ChakraV3.Dialog.Content>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
